import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Loader2, Inbox, CheckCheck } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { useNotifications } from '@/contexts/NotificationsContext';
import { SEVERITY_META } from '@/pages/monitoring/components/SeverityBadge';

const NOTIFICATIONS_URL = '/monitor-registros?tab=notificaciones';

const fmtFecha = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleString('es-CO', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Campana de notificaciones de monitoreo. El conteo lo mantiene
 * NotificationsContext (polling cada 60 s); aquí solo se pinta y se enlaza a la
 * bandeja completa. Si el usuario no tiene permiso, no se renderiza nada.
 */
const NotificationBell = () => {
  const navigate = useNavigate();
  const { noLeidas, recientes, loading, canView, markRead } = useNotifications();

  if (!canView) return null;

  const irABandeja = () => navigate(NOTIFICATIONS_URL);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="relative rounded-lg p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={
            noLeidas > 0 ? `${noLeidas} notificaciones sin leer` : 'Notificaciones'
          }
        >
          <Bell className="h-5 w-5" />
          {noLeidas > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold leading-none text-destructive-foreground tabular-nums">
              {noLeidas > 99 ? '99+' : noLeidas}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b border-border px-3 py-2">
          <span className="text-sm font-semibold">Monitoreo</span>
          {loading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
          ) : (
            noLeidas > 0 && (
              <button
                type="button"
                onClick={() => markRead({ all: true })}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
              >
                <CheckCheck className="h-3.5 w-3.5" />
                Marcar leídas
              </button>
            )
          )}
        </div>

        <div className="max-h-80 overflow-y-auto">
          {recientes.length === 0 ? (
            <div className="px-3 py-8 text-center">
              <Inbox className="mx-auto mb-2 h-6 w-6 text-muted-foreground/40" />
              <p className="text-xs text-muted-foreground">Sin novedades pendientes.</p>
            </div>
          ) : (
            recientes.map((n) => {
              const meta = SEVERITY_META[n.severidad] || SEVERITY_META.info;
              return (
                <button
                  key={n._id}
                  type="button"
                  onClick={irABandeja}
                  className="flex w-full items-start gap-2.5 border-b border-border/50 px-3 py-2.5 text-left transition-colors last:border-0 hover:bg-accent/50"
                >
                  <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${meta.dot}`} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-xs font-medium text-foreground">
                      {n.titulo}
                    </span>
                    <span className="mt-0.5 block line-clamp-2 text-xs text-muted-foreground">
                      {n.mensaje}
                    </span>
                    <span className="mt-1 block text-[10px] text-muted-foreground/70">
                      {n.lavanderia?.nombre} · {fmtFecha(n.createdAt)}
                    </span>
                  </span>
                </button>
              );
            })
          )}
        </div>

        <div className="border-t border-border p-2">
          <Button variant="ghost" size="sm" className="w-full" onClick={irABandeja}>
            Ver todas
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationBell;
